// src/features/media/components/MergeSortableList.tsx

import { useCallback, useMemo } from 'react';
import { Stack, Group, Text, ActionIcon, Image, Box } from '@mantine/core';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MediaUtils } from '../MediaUtils';
import type { VideoViewData } from '@/lib/types/Media';
import type { SharedLinkConfig } from '@/lib/types/link';

export interface MergeListItem {
  mediaId: number;
  title: string;
  duration: number;
  thumbnailUrl: string | null;
}

interface MergeSortableListProps {
  items: MergeListItem[];
  onReorder: (newOrder: number[]) => void;
  onRemove: (mediaId: number) => void;
}

export function MergeSortableList({ items, onReorder, onRemove }: MergeSortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const sortableIds = useMemo(() => items.map(i => i.mediaId), [items]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(i => i.mediaId === active.id);
    const newIndex = items.findIndex(i => i.mediaId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    onReorder(reordered.map(i => i.mediaId));
  }, [items, onReorder]);

  return (
    <Stack gap={0}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem
              key={item.mediaId}
              item={item}
              position={index + 1}
              onRemove={() => onRemove(item.mediaId)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Text size="xs" c="dimmed" ta="center" mt="sm">
        Drag to reorder
      </Text>
    </Stack>
  );
}

/**
 * Sortable item using @dnd-kit/sortable.
 * The item itself lifts and moves with the cursor when dragged.
 * setActivatorNodeRef limits drag initiation to the grip handle.
 */
function SortableItem({
  item,
  position,
  onRemove,
}: {
  item: MergeListItem;
  position: number;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.mediaId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? 'relative' as const : undefined,
    boxShadow: isDragging ? 'var(--mantine-shadow-md)' : undefined,
    background: isDragging ? 'white' : undefined,
    borderRadius: isDragging ? 'var(--mantine-radius-sm)' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Group
        gap="sm"
        wrap="nowrap"
        px="sm"
        py="xs"
        style={{ borderBottom: isDragging ? undefined : '1px solid var(--mantine-color-gray-2)' }}
      >
        <ActionIcon
          ref={setActivatorNodeRef}
          {...listeners}
          variant="subtle"
          color="gray"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          aria-label="Drag to reorder"
        >
          <IconGripVertical size={16} />
        </ActionIcon>

        <Text size="xs" c="dimmed" w={24} ta="right">{position}</Text>

        {item.thumbnailUrl ? (
          <Image
            src={item.thumbnailUrl}
            w={64}
            h={36}
            radius="xs"
            style={{ flexShrink: 0 }}
            alt=""
          />
        ) : (
          <Box
            w={64}
            h={36}
            bg="gray.1"
            style={{ borderRadius: 'var(--mantine-radius-xs)', flexShrink: 0 }}
          />
        )}

        {/* minWidth: 0 lets the nowrap title actually shrink and truncate —
            flexbox min-width:auto otherwise forces the row wider than narrow
            viewports and pushes duration + remove off-screen. Remove is always
            available (footer validation guards the 2-video minimum); hiding it
            at exactly 2 items stranded users who picked one wrong video. */}
        <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate="end">{item.title}</Text>

        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {MediaUtils.formatDuration(item.duration)}
        </Text>

        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onRemove}
          aria-label={`Remove ${item.title}`}
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
    </div>
  );
}

/**
 * Build MergeListItem array from video data for the sortable list.
 */
export function buildMergeListItems(
  orderedMediaIds: number[],
  videosById: Map<number, VideoViewData>,
  linkConfig: SharedLinkConfig | null
): MergeListItem[] {
  return orderedMediaIds.map(id => {
    const video = videosById.get(id)!;
    return {
      mediaId: id,
      title: video.media.title,
      duration: video.media.duration,
      thumbnailUrl: MediaUtils.getThumbnailUrl(linkConfig, video),
    };
  });
}
